<template>
    <c-dialog ref="dialog" mode="slide" width="60vw" enable-flex mask-close>
        <div class="channel-authorization">
            <div v-if="loading" class="tip">加载中...</div>

            <div v-else-if="!info" class="tip error">
                {{ errorMessage || '未获取到授权信息' }}
            </div>

            <div v-else>
                <!-- 授权链接生成 -->
                <div class="section">
                    <div class="section-title">生成授权链接</div>
                    <c-form v-if="info.need_user_params" unit-width="50%">
                        <c-form-item label="用户名">
                            <c-input v-model="form.nickName" maxlength="50" :clear="false" placeholder="被授权人在平台的昵称"></c-input>
                        </c-form-item>
                        <c-form-item label="手机号">
                            <c-input v-model="form.phone" maxlength="20" :clear="false" placeholder="被授权人手机号"></c-input>
                        </c-form-item>
                    </c-form>
                    <div class="url-row">
                        <span class="label">授权链接：</span>
                        <c-input class="url-ipt" :value="generatedUrl" disabled placeholder="填写用户名和手机号后自动生成"></c-input>
                    </div>
                    <div class="action-row">
                        <c-button :disabled="!generatedUrl" @click="copyUrl">复制链接</c-button>
                        <c-button color="main" :disabled="!generatedUrl" @click="openUrl">打开授权页</c-button>
                    </div>
                </div>

                <!-- 平台账号池 -->
                <div class="section">
                    <div class="section-head">
                        <div class="section-title">平台已授权账号池</div>
                        <c-button class="refresh-btn" :disabled="poolRefreshing" @click="refreshPool">{{ poolRefreshing ? '刷新中...' : '刷新' }}</c-button>
                    </div>
                    <div class="pool-tip">授权完成后由平台回调同步到此处，可绑定到当前渠道；一个平台可能有多个账号</div>
                    <c-table :data="poolAccounts" border :paging="false" empty-text="暂无账号，请先通过上方链接完成授权">
                        <c-table-column label="用户名" name="username" min-width="160"></c-table-column>
                        <c-table-column label="手机号" width="140">
                            <template #default="{data}">{{ data.phone || '-' }}</template>
                        </c-table-column>
                        <c-table-column label="已绑定渠道" min-width="180">
                            <template #default="{data}">{{ data.boundChannels || '未绑定' }}</template>
                        </c-table-column>
                        <c-table-column label="授权到期时间" width="180">
                            <template #default="{data}">
                                <span :class="{'expire-danger': isExpired(data.authorizationExpiresAt)}">{{ formatExpires(data.authorizationExpiresAt) }}</span>
                            </template>
                        </c-table-column>
                        <c-table-column label="同步时间" width="180">
                            <template #default="{data}">{{ formatDate(data.updateDate) }}</template>
                        </c-table-column>
                        <c-table-column label="操作" type="button" width="160">
                            <template #default="{data}">
                                <span v-if="isCurrentBound(data)" class="badge-current">当前绑定</span>
                                <c-table-button v-else icon="tf-bind" @click="bindAccount(data)">绑定到当前渠道</c-table-button>
                            </template>
                        </c-table-column>
                    </c-table>
                </div>

                <!-- 当前渠道绑定信息 -->
                <div class="section">
                    <div class="section-title">当前渠道绑定</div>
                    <div v-if="!info.bound_account" class="tip">未绑定任何账号</div>
                    <div v-else class="bound-row">
                        <span class="bound-name">{{ info.bound_account.username }}</span>
                        <span class="bound-phone">{{ info.bound_account.phone || '-' }}</span>
                        <c-button class="unbind-btn" @click="unbindAccount">解绑</c-button>
                    </div>
                </div>
            </div>
        </div>
    </c-dialog>
</template>

<script>
export default {
    name: 'channelAuthorization',

    data() {
        return {
            loading: false,
            poolRefreshing: false,
            channelId: 0,
            channelName: '',
            info: null,
            errorMessage: '',
            form: {
                nickName: '',
                phone: ''
            },
            generatedUrl: ''
        };
    },

    computed: {
        poolAccounts() {
            return (this.info && this.info.pool_accounts) || [];
        }
    },

    watch: {
        /*********用户名/手机号变化时实时刷新链接*********/
        'form.nickName'() {
            this.syncUrl();
        },
        'form.phone'() {
            this.syncUrl();
        }
    },

    methods: {
        /*********打开弹框，初始化*********/
        open(data) {
            this.channelId = data && data.id ? data.id : 0;
            this.channelName = data && data.channel_name ? data.channel_name : '';
            this.info = null;
            this.errorMessage = '';
            this.poolRefreshing = false;
            this.form = {nickName: '', phone: ''};
            this.generatedUrl = '';
            this.$refs.dialog.open({title: '渠道授权 - ' + this.channelName});
            this.loadInfo();
        },

        /*********加载授权信息*********/
        loadInfo() {
            this.loading = true;
            this.request({
                url: '/channel/authorization/info',
                data: {channel_id: this.channelId},
                success: (data) => {
                    this.loading = false;
                    this.info = data;
                    // 若已绑定，用绑定账号预填 nickName/phone，方便快速生成同账号的新链接
                    if (data && data.bound_account) {
                        this.form.nickName = data.bound_account.username || '';
                        this.form.phone = data.bound_account.phone || '';
                    }
                    this.syncUrl();
                },
                fail: (code, obj, message) => {
                    this.loading = false;
                    this.errorMessage = message || '获取授权信息失败';
                    // 返回 false 阻止组件库默认 toast，错误统一在面板内展示
                    return false;
                }
            });
        },

        /*********刷新账号池：重新拉一次授权信息，只同步账号池和当前绑定，不覆盖用户正在填的表单和已生成的链接*********/
        refreshPool() {
            if (this.poolRefreshing) return;
            this.poolRefreshing = true;
            this.request({
                url: '/channel/authorization/info',
                data: {channel_id: this.channelId},
                success: (data) => {
                    this.poolRefreshing = false;
                    if (!data || !this.info) return;
                    this.$set(this.info, 'pool_accounts', data.pool_accounts || []);
                    this.$set(this.info, 'bound_account', data.bound_account || null);
                    this.$message({message: '已获取最新账号数据', type: 'success'});
                },
                fail: () => {
                    // 不返回 false，失败提示交给组件库默认 toast
                    this.poolRefreshing = false;
                }
            });
        },

        /*********根据模板 + 输入实时生成链接*********/
        syncUrl() {
            if (!this.info || !this.info.auth_url_template) {
                this.generatedUrl = '';
                return;
            }
            let url = this.info.auth_url_template;
            if (this.info.need_user_params) {
                const nick = encodeURIComponent(this.form.nickName || '');
                const phone = encodeURIComponent(this.form.phone || '');
                url = url.replace(/\{nickName\}/g, nick).replace(/\{phone\}/g, phone);
                // 未填完整时不展示链接，避免生成不合法 URL
                if (!this.form.nickName || !this.form.phone) {
                    this.generatedUrl = '';
                    return;
                }
            }
            this.generatedUrl = url;
        },

        /*********复制链接（clipboard 优先，降级 execCommand）*********/
        copyUrl() {
            const text = this.generatedUrl;
            if (!text) return;
            const done = () => this.$message({message: '授权链接已复制', type: 'success'});
            const fallback = () => {
                const ipt = document.createElement('input');
                ipt.value = text;
                document.body.appendChild(ipt);
                ipt.select();
                try {
                    document.execCommand('copy');
                    done();
                } catch (e) {
                    this.$message({message: '复制失败，请手动选中链接复制', type: 'warn'});
                }
                document.body.removeChild(ipt);
            };
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).then(done).catch(fallback);
            } else {
                fallback();
            }
        },

        /*********新窗口打开授权页；被拦截时降级为复制*********/
        openUrl() {
            const url = this.generatedUrl;
            if (!url) return;
            const win = window.open(url, '_blank', 'noopener,noreferrer');
            if (!win) {
                this.$message({message: '浏览器已拦截弹窗，链接已复制到剪贴板，请手动粘贴到浏览器地址栏', type: 'warn'});
                this.copyUrl();
            }
        },

        /*********判断池账号是否为当前渠道已绑定账号*********/
        isCurrentBound(acc) {
            const bound = this.info && this.info.bound_account;
            return !!bound && bound.username === acc.username;
        },

        /*********绑定：从池挑一个账号 upsert 到 tbl_channel_platform_account*********/
        bindAccount(acc) {
            this.$confirm({
                type: 'warn',
                message: `确认将账号【${acc.username}】绑定到当前渠道？若已有绑定将被替换。`,
                buttonName: '确认绑定',
                cancelButton: true,
                resolve: () => {
                    this.request({
                        url: '/channel/account/bind',
                        data: {
                            channel_id: this.channelId,
                            username: acc.username,
                            phone: acc.phone || ''
                        },
                        loading: true,
                        success: () => {
                            this.$message({message: '绑定成功', type: 'success'});
                            this.loadInfo();
                        }
                    });
                }
            });
        },

        /*********解绑：删除当前渠道绑定记录*********/
        unbindAccount() {
            this.$confirm({
                type: 'warn',
                message: '确认解绑当前渠道的平台账号？解绑后该渠道将无法推送。',
                buttonName: '确认解绑',
                cancelButton: true,
                resolve: () => {
                    this.request({
                        url: '/channel/account/unbind',
                        data: {channel_id: this.channelId},
                        loading: true,
                        success: () => {
                            this.$message({message: '解绑成功', type: 'success'});
                            this.loadInfo();
                        }
                    });
                }
            });
        },

        /*********时间格式化：yyyy-MM-dd HH:mm*********/
        formatDate(v) {
            if (!v) return '-';
            const d = new Date(v);
            if (isNaN(d.getTime())) return v;
            const pad = n => (n < 10 ? '0' + n : '' + n);
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
        },

        /*********授权到期时间展示：空=长期有效*********/
        formatExpires(v) {
            if (!v) return '长期有效';
            return this.formatDate(v);
        },

        /*********是否已过期（无到期时间视为不过期）*********/
        isExpired(v) {
            if (!v) return false;
            const d = new Date(v);
            return !isNaN(d.getTime()) && d.getTime() < Date.now();
        }
    }
};
</script>

<style lang="stylus" scoped>
.channel-authorization
    padding 20px

.tip
    padding 24px 0
    text-align center
    color #999
    &.error
        color #f56c6c

.section
    margin-bottom 24px
    padding-bottom 20px
    border-bottom 1px dashed #eee
    &:last-child
        border-bottom none

.section-title
    font-size 15px
    font-weight 600
    margin-bottom 12px

.section-head
    display flex
    align-items center
    justify-content space-between
    margin-bottom 12px
    .section-title
        margin-bottom 0
    .refresh-btn
        font-size 12px

.url-row
    display flex
    align-items center
    margin 12px 0
    .label
        width 82px
        text-align right
        color #666
        flex-shrink 0
    .url-ipt
        flex 1

.action-row
    display flex
    gap 12px

.pool-tip
    color #999
    font-size 12px
    margin-bottom 8px

.badge-current
    display inline-block
    padding 2px 8px
    background #67c23a
    color #fff
    border-radius 3px
    font-size 12px

.expire-danger
    color #f56c6c
    font-weight 600

.bound-row
    display flex
    align-items center
    padding 8px 12px
    background #f0f9eb
    border-radius 4px
    .bound-name
        font-weight 600
    .bound-phone
        margin-left 16px
        color #666
    .unbind-btn
        margin-left auto
</style>
